import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useAuth } from '@/lib/auth-store';

/**
 * Customer context store
 * ─────────────────────────────────────────────────────────────────────
 * Super-admin and reseller_owner users don't have an inherent customer
 * scope — they manage many. Rather than forcing every create-dialog to
 * show a "pick the customer" select (the old <CustomerPicker /> pattern),
 * those users "enter" a customer once from the customers list and the
 * entire portal then operates inside that customer's scope.
 *
 * Behaviour:
 *  - `enter(customer)` stores the current customer. All customer-scoped
 *    pages pick up this id via useCustomerPicker / RequireCustomerContext.
 *  - `exit()` clears it. The user goes back to a "choose a customer"
 *    state (customers list page).
 *  - The value is persisted to localStorage so F5 doesn't kick the user
 *    back out.
 *
 * For office_manager / standard_user / limited_user this store is
 * essentially unused — those roles have a single tenant.customerId and
 * useCustomerPicker reads from auth-store directly.
 */

export interface CurrentCustomer {
  id: string;
  name: string;
  resellerId: string;
  customerNumber: number | null;
}

interface CustomerContextState {
  current: CurrentCustomer | null;
  enter: (customer: CurrentCustomer) => void;
  exit: () => void;
}

export const useCustomerContext = create<CustomerContextState>()(
  persist(
    (set) => ({
      current: null,
      enter: (customer) => set({ current: customer }),
      exit: () => set({ current: null }),
    }),
    {
      name: 'ivrello:customer-context',
      storage: createJSONStorage(() => localStorage),
      // Only persist the current customer — actions are restored on mount.
      partialize: (s) => ({ current: s.current }),
      version: 1,
    },
  ),
);

/** Convenience selectors so call sites don't have to spell them out. */
export const selectCurrentCustomerId = (s: CustomerContextState) =>
  s.current?.id ?? null;

export const selectCurrentCustomerName = (s: CustomerContextState) =>
  s.current?.name ?? null;

export const selectCurrentCustomerResellerId = (s: CustomerContextState) =>
  s.current?.resellerId ?? null;

/**
 * useEffectiveCustomerId
 * ─────────────────────────────────────────────────────────────────────
 * Returns the customer ID that should be used to scope API list calls.
 *
 * - For super_admin / reseller_owner: returns the ID they've "entered" via
 *   the customer context store. Returns null if they haven't entered one yet
 *   (RequireCustomerContext will already redirect them, so null here means
 *   we're on a cross-customer surface and the param should be omitted).
 * - For all other roles (office_manager, standard_user, limited_user, agent):
 *   returns their own tenant customerId from the JWT, which is always set.
 *
 * Pass the returned ID as `?customer_id=<uuid>` on every customer-scoped
 * list request so the backend filters correctly even for SA/reseller_owner.
 */
export function useEffectiveCustomerId(): string | null {
  const user = useAuth((s) => s.user);
  const tenant = useAuth((s) => s.tenant);
  const contextCustomerId = useCustomerContext((s) => s.current?.id ?? null);

  if (!user) return null;

  const needsContext = user.role === 'super_admin' || user.role === 'reseller_owner';
  if (needsContext) {
    // SA/reseller_owner: use the entered customer context.
    return contextCustomerId;
  }

  // Lower roles: their customerId is baked into the JWT tenant scope.
  return tenant?.customerId ?? null;
}
